import React, { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Maximize2, X } from 'lucide-react'
import type { ShowcaseLayout, ShowcaseSection } from '../../showcase/types'

const lightboxTransition = {
  type: 'tween' as const,
  ease: [0.25, 0.46, 0.45, 0.94],
  duration: 0.28,
}

const lightboxBackdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
}

const lightboxImageVariants = {
  hidden: { opacity: 0, scale: 0.96, y: 16 },
  visible: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.98, y: 8 },
}

function ShowcaseImageLightbox({
  src,
  onClose,
}: {
  src: string
  onClose: () => void
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <motion.div
      className="fixed inset-0 z-[100] flex items-center justify-center p-6 md:p-10"
      variants={lightboxBackdropVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      transition={{ duration: 0.22 }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Enlarged image"
    >
      <div className="absolute inset-0 bg-black/75" aria-hidden />
      <motion.button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 z-10 flex h-9 w-9 items-center justify-center border border-white/30 bg-black/50 text-white hover:bg-black/70 transition-colors"
        aria-label="Close"
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ ...lightboxTransition, delay: 0.06 }}
      >
        <X className="h-4 w-4" strokeWidth={1.5} />
      </motion.button>
      <motion.img
        src={src}
        alt=""
        className="relative z-[1] max-h-full max-w-full object-contain grayscale contrast-125"
        variants={lightboxImageVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        transition={lightboxTransition}
        onClick={(e) => e.stopPropagation()}
      />
    </motion.div>
  )
}

function HeroMedia({ url, kind }: { url: string; kind: string }) {
  if (kind === 'video') {
    return (
      <div className="w-full overflow-hidden border border-black/10 bg-neutral-100 aspect-[21/9] md:aspect-[2.4/1]">
        <video
          src={url}
          className="w-full h-full object-cover grayscale contrast-125"
          controls
          playsInline
        />
      </div>
    )
  }
  return <ShowcaseImage src={url} className="aspect-[21/9] md:aspect-[2.4/1]" />
}

function ShowcaseImage({
  src,
  className = '',
}: {
  src: string
  className?: string
}) {
  const [failed, setFailed] = useState(false)
  const [enlarged, setEnlarged] = useState(false)

  if (failed) {
    return (
      <div
        className={`w-full border border-black/10 bg-neutral-100 flex items-center justify-center text-xs uppercase tracking-widest text-black/40 cursor-default ${className}`}
      >
        Image unavailable
      </div>
    )
  }
  return (
    <>
      <div
        className={`group relative w-full overflow-hidden border border-black/10 bg-neutral-100 ${className}`}
      >
        <img
          src={src}
          alt=""
          className="w-full h-full object-cover grayscale contrast-125"
          onError={() => setFailed(true)}
        />
        <button
          type="button"
          onClick={() => setEnlarged(true)}
          className="absolute bottom-2 right-2 z-10 flex h-8 w-8 items-center justify-center border border-black/15 bg-white/90 text-black opacity-0 pointer-events-none transition-opacity duration-200 group-hover:opacity-100 group-hover:pointer-events-auto hover:bg-white focus:opacity-100 focus:pointer-events-auto"
          aria-label="Enlarge image"
        >
          <Maximize2 className="h-3.5 w-3.5" strokeWidth={1.5} />
        </button>
      </div>
      <AnimatePresence>
        {enlarged && (
          <ShowcaseImageLightbox
            key="showcase-lightbox"
            src={src}
            onClose={() => setEnlarged(false)}
          />
        )}
      </AnimatePresence>
    </>
  )
}

function TextBlock({
  paragraphs,
  align,
}: {
  paragraphs: string[]
  align: 'left' | 'center'
}) {
  const alignClass = align === 'center' ? 'text-center' : 'text-left'
  return (
    <div
      className={`space-y-6 text-xs md:text-sm leading-relaxed tracking-wide uppercase text-black/90 cursor-default ${alignClass}`}
    >
      {paragraphs.map((paragraph, i) => (
        <p key={i}>{paragraph}</p>
      ))}
    </div>
  )
}

function MediaBlock({ media }: { media: { url: string; kind: string } }) {
  if (media.kind === 'video') {
    return (
      <div className="w-full overflow-hidden border border-black/10 bg-black aspect-[21/6] md:aspect-[24/5]">
        <video
          src={media.url}
          className="w-full h-full object-contain"
          controls
          playsInline
        />
      </div>
    )
  }
  return (
    <ShowcaseImage
      src={media.url}
      className="aspect-[21/6] md:aspect-[24/5]"
    />
  )
}

function ImageGrid({ items }: { items: { url: string }[] }) {
  const cols = items.length >= 4 ? 2 : items.length === 3 ? 2 : items.length
  return (
    <div
      className={`grid gap-3 md:gap-4 ${
        cols >= 2 ? 'grid-cols-2' : 'grid-cols-1'
      }`}
    >
      {items.map((item) => (
        <ShowcaseImage
          key={item.url}
          src={item.url}
          className="aspect-square md:aspect-[4/3]"
        />
      ))}
    </div>
  )
}

function SectionView({ section }: { section: ShowcaseSection }) {
  switch (section.type) {
    case 'hero-media':
      return (
        <HeroMedia url={section.media.url} kind={section.media.kind} />
      )
    case 'text-block':
      return (
        <TextBlock paragraphs={section.paragraphs} align={section.align} />
      )
    case 'media-block':
      return <MediaBlock media={section.media} />
    case 'image-grid':
      return <ImageGrid items={section.items} />
    case 'audio-block':
      return (
        <div className="border border-black/10 p-4 bg-neutral-50">
          <audio src={section.media.url} controls className="w-full" />
          <p className="mt-2 text-[10px] uppercase tracking-widest text-black/50 cursor-default">
            {section.media.filename}
          </p>
        </div>
      )
    case 'file-link':
      return (
        <a
          href={section.media.url}
          target="_blank"
          rel="noopener noreferrer"
          className="block border border-black/10 px-4 py-3 text-xs uppercase tracking-widest text-black/70 hover:text-black hover:border-black/30 transition-colors"
        >
          Download {section.media.filename}
        </a>
      )
    default:
      return null
  }
}

export function ShowcaseRenderer({ layout }: { layout: ShowcaseLayout }) {
  return (
    <div className="space-y-10 md:space-y-14">
      {layout.sections.map((section, index) => (
        <div key={`${section.type}-${index}`}>
          <SectionView section={section} />
        </div>
      ))}
    </div>
  )
}
